import { useCallback } from 'react';
import type { InfiniteData } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';

import type { GetCommunicationsResponse } from '@izimi/api';

import CommunicationsQueryKeys from '../CommunicationsQueryKeys';
import type CommunicationDetailsEntity from './CommunicationDetailsEntity';
import { isCommunication } from './mappers';

const communicationQueryKeys = new CommunicationsQueryKeys();

type CommunicationDocumentsCopyParams = { communicationId: string; documentIds: Array<string> };

/**
 * @deprecated Part of the entity-based communications module. Use the new
 * `createCommunicationsModule` cache handling instead.
 */
export const useOptimisticCommunicationDocumentsCopyCacheUpdate = () => {
  const queryClient = useQueryClient();

  const onMutate = useCallback(
    async ({ communicationId, documentIds }: CommunicationDocumentsCopyParams) => {
      const queryKey = communicationQueryKeys.getCommunication(communicationId);
      await queryClient.cancelQueries({ queryKey });
      const previousCommunicationData: CommunicationDetailsEntity | undefined =
        queryClient.getQueryData(queryKey);

      if (!isCommunication(previousCommunicationData)) {
        return {};
      }

      const nextData = {
        ...previousCommunicationData,
        documents: previousCommunicationData.documents.map((doc) => {
          if (documentIds.includes(doc.documentId)) {
            return {
              ...doc,
              isAvailable: false,
            };
          }
          return doc;
        }),
      };

      queryClient.setQueryData(queryKey, nextData);

      return { previousCommunicationData };
    },
    [queryClient],
  );

  const onError = useCallback(
    async (
      _: unknown,
      { communicationId }: CommunicationDocumentsCopyParams,
      context: { previousCommunicationData?: CommunicationDetailsEntity } | undefined,
    ) => {
      if (context?.previousCommunicationData) {
        const queryKey = communicationQueryKeys.getCommunication(communicationId);
        queryClient.setQueryData(queryKey, context.previousCommunicationData);
      }
    },
    [queryClient],
  );

  return {
    onMutate,
    onError,
  };
};

/**
 * @deprecated Part of the entity-based communications module. Use the new
 * `createCommunicationsModule` cache handling instead.
 */
export const useOptimisticCommunicationsDocumentsCopyCacheUpdate = () => {
  const queryClient = useQueryClient();

  const onMutate = useCallback(
    async ({ communicationId, documentIds }: CommunicationDocumentsCopyParams) => {
      const queryKey = communicationQueryKeys.getCommunications('communications');
      await queryClient.cancelQueries({ queryKey });
      const previousCommunicationsData:
        | InfiniteData<GetCommunicationsResponse, unknown>
        | undefined = queryClient.getQueryData(queryKey);

      if (!previousCommunicationsData) {
        return {};
      }

      const updatedData = {
        ...previousCommunicationsData,
        pages: previousCommunicationsData.pages.map((page) => ({
          ...page,
          items: page.communications.map((item) => {
            if (item.id === communicationId && item.type === 'document') {
              return {
                ...item,
                documents: item.documents.map((document) => {
                  if (documentIds.includes(document.documentId)) {
                    return { ...document, isAvailable: false };
                  }

                  return document;
                }),
              };
            }

            return item;
          }),
        })),
      };

      queryClient.setQueryData(queryKey, updatedData);

      return { previousCommunicationsData };
    },
    [queryClient],
  );

  const onError = useCallback(
    async (
      _: unknown,
      __: CommunicationDocumentsCopyParams,
      context:
        | {
            previousCommunicationsData?:
              | InfiniteData<GetCommunicationsResponse, unknown>
              | undefined;
          }
        | undefined,
    ) => {
      if (context?.previousCommunicationsData) {
        const queryKey = communicationQueryKeys.getCommunications('communications');
        queryClient.setQueryData(queryKey, context.previousCommunicationsData);
      }
    },
    [queryClient],
  );

  return {
    onMutate,
    onError,
  };
};
